import { CheckCircle2, Circle } from 'lucide-react';

import { cn } from 'src/lib/utils';

export const PASSWORD_REQUIREMENTS: { label: string; test: (password: string) => boolean }[] = [
  { label: 'At least 8 characters', test: (password) => password.length >= 8 },
  { label: 'One uppercase letter', test: (password) => /[A-Z]/.test(password) },
  { label: 'One lowercase letter', test: (password) => /[a-z]/.test(password) },
  { label: 'One number', test: (password) => /\d/.test(password) },
  { label: 'One special character', test: (password) => /[^a-zA-Z0-9]/.test(password) },
];

const STRENGTH_BAR_SEGMENTS = 4;

const getStrengthColor = (score: number) => {
  if (score < 2) return 'bg-red-500';
  if (score < 3) return 'bg-amber-500';
  return 'bg-emerald-500';
};

type Props = {
  password: string;
  hasError?: boolean;
};

const PasswordRequirements = ({ password, hasError }: Props) => {
  const score = PASSWORD_REQUIREMENTS.filter(({ test }) => test(password)).length;
  const strengthColor = getStrengthColor(score);

  return (
    <>
      <div className="flex gap-1" aria-hidden="true">
        {Array.from({ length: STRENGTH_BAR_SEGMENTS }, (_, i) => (
          <div key={i} className={cn('h-1 flex-1 rounded-full', i < score ? strengthColor : 'bg-muted')} />
        ))}
      </div>

      <ul className="space-y-1.5 text-xs">
        {PASSWORD_REQUIREMENTS.map(({ label, test }) => {
          const met = test(password);

          return (
            <li
              key={label}
              className={cn(
                'flex items-center gap-2',
                met ? 'text-emerald-600' : hasError ? 'text-destructive line-through' : 'text-muted-foreground',
              )}
            >
              {met ? (
                <CheckCircle2 className="size-3.5 shrink-0" aria-label={`${label} requirement met`} />
              ) : (
                <Circle className="size-3.5 shrink-0" aria-label={`${label} requirement not met`} />
              )}
              {label}
            </li>
          );
        })}
      </ul>
    </>
  );
};

export default PasswordRequirements;
